/**
 * Pending Transaction Tracker
 * Tracks transactions initiated but not yet confirmed by the backend
 * Handles browser/app context switches (especially on mobile)
 */

const STORAGE_KEY = 'goat_pending_transactions';
const MAX_PENDING_TIME = 24 * 60 * 60 * 1000; // 24 hours

export class PendingTransactionTracker {
  /**
   * Add a pending transaction
   */
  static addPending(transactionData) {
    try {
      const pending = this.getPendingTransactions();
      
      const pendingTx = {
        ...transactionData,
        txHash: transactionData.txHash || null,
        status: 'pending_confirmation',
        initiatedAt: Date.now(),
        attempts: 0,
        lastAttempt: null,
        lastError: null
      };

      // Use a unique key combining wallet + timestamp
      const key = `${transactionData.walletAddress}_${Date.now()}`;
      pending[key] = pendingTx;

      localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
      console.log(`[PENDING-TX] Added pending transaction:`, key);

      return key;
    } catch (error) {
      console.error('[PENDING-TX] Error adding pending transaction:', error);
      return null;
    }
  }

  /**
   * Update a pending transaction with tx hash
   */
  static updateWithHash(txKey, txHash) {
    try {
      const pending = this.getPendingTransactions();
      
      if (pending[txKey]) {
        pending[txKey].txHash = txHash;
        pending[txKey].status = 'hash_received';
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
        console.log(`[PENDING-TX] Updated with hash: ${txHash.slice(0, 10)}...`);
      }
    } catch (error) {
      console.error('[PENDING-TX] Error updating with hash:', error);
    }
  }

  /**
   * Mark transaction as confirmed in DB
   */
  static markConfirmed(txKey) {
    try {
      const pending = this.getPendingTransactions();
      
      if (pending[txKey]) {
        pending[txKey].status = 'confirmed';
        pending[txKey].confirmedAt = Date.now();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
        console.log(`[PENDING-TX] Marked as confirmed: ${txKey}`);
      }
    } catch (error) {
      console.error('[PENDING-TX] Error marking as confirmed:', error);
    }
  }

  /**
   * Mark transaction as failed
   */
  static markFailed(txKey, error) {
    try {
      const pending = this.getPendingTransactions();
      
      if (pending[txKey]) {
        pending[txKey].status = 'failed';
        pending[txKey].lastError = error;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
        console.log(`[PENDING-TX] Marked as failed: ${txKey}`, error);
      }
    } catch (error) {
      console.error('[PENDING-TX] Error marking as failed:', error);
    }
  }

  /**
   * Get all pending transactions
   */
  static getPendingTransactions() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('[PENDING-TX] Error reading pending transactions:', error);
      return {};
    }
  }

  /**
   * Get unconfirmed pending transactions that need recovery
   */
  static getUnconfirmedTransactions() {
    try {
      const pending = this.getPendingTransactions();
      const now = Date.now();
      const unconfirmed = [];

      Object.entries(pending).forEach(([key, tx]) => {
        // Check if transaction needs attention
        const timePassed = now - tx.initiatedAt;
        const isExpired = timePassed > MAX_PENDING_TIME;
        const needsConfirmation = tx.status === 'hash_received' && tx.txHash;

        if (!isExpired && needsConfirmation) {
          unconfirmed.push({
            key,
            ...tx
          });
        }
      });

      return unconfirmed;
    } catch (error) {
      console.error('[PENDING-TX] Error getting unconfirmed transactions:', error);
      return [];
    }
  }

  /**
   * Get by transaction hash
   */
  static getByHash(txHash) {
    try {
      const pending = this.getPendingTransactions();
      
      for (const [key, tx] of Object.entries(pending)) {
        if (tx.txHash === txHash) {
          return { key, ...tx };
        }
      }
      
      return null;
    } catch (error) {
      console.error('[PENDING-TX] Error getting transaction by hash:', error);
      return null;
    }
  }

  /**
   * Record an attempt to confirm the transaction
   */
  static recordAttempt(txKey, error = null) {
    try {
      const pending = this.getPendingTransactions();
      
      if (pending[txKey]) {
        pending[txKey].attempts += 1;
        pending[txKey].lastAttempt = Date.now();
        if (error) {
          pending[txKey].lastError = error;
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
      }
    } catch (error) {
      console.error('[PENDING-TX] Error recording attempt:', error);
    }
  }

  /**
   * Clean up old transactions
   */
  static cleanup() {
    try {
      const pending = this.getPendingTransactions();
      const now = Date.now();
      const cleaned = {};

      Object.entries(pending).forEach(([key, tx]) => {
        const timePassed = now - tx.initiatedAt;
        
        // Keep only recent transactions or unconfirmed ones
        if (timePassed < MAX_PENDING_TIME && tx.status !== 'confirmed' && tx.status !== 'failed') {
          cleaned[key] = tx;
        }
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
      console.log(`[PENDING-TX] Cleanup complete`);
    } catch (error) {
      console.error('[PENDING-TX] Error cleaning up:', error);
    }
  }

  /**
   * Remove a transaction
   */
  static remove(txKey) {
    try {
      const pending = this.getPendingTransactions();
      delete pending[txKey];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
      console.log(`[PENDING-TX] Removed: ${txKey}`);
    } catch (error) {
      console.error('[PENDING-TX] Error removing transaction:', error);
    }
  }

  /**
   * Clear all pending transactions
   */
  static clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log(`[PENDING-TX] Cleared all pending transactions`);
    } catch (error) {
      console.error('[PENDING-TX] Error clearing:', error);
    }
  }
}

export default PendingTransactionTracker;
